// src/hooks/useAdmin.js
import { useState, useEffect, useCallback } from 'react';
import { 
    getAllJobsAdmin, 
    addJob, 
    updateJob, 
    deleteJob,
    getAllBlogsAdmin, 
    addBlog, 
    updateBlog, 
    deleteBlog,
    getAllUsers, 
    deleteUser,
    getDashboardStats
} from '../services/admin';

export const useAdmin = () => {
    const [jobs, setJobs] = useState([]);
    const [blogs, setBlogs] = useState([]);
    const [users, setUsers] = useState([]);
    const [stats, setStats] = useState({});
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('dashboard');

    const loadAllData = useCallback(async () => {
        setLoading(true);
        try {
            const [jobsData, blogsData, usersData, statsData] = await Promise.all([
                getAllJobsAdmin(),
                getAllBlogsAdmin(),
                getAllUsers(),
                getDashboardStats()
            ]);
            setJobs(jobsData);
            setBlogs(blogsData);
            setUsers(usersData);
            setStats(statsData);
        } catch (error) {
            console.error("Error loading admin data:", error);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadAllData();
    }, [loadAllData]);

    // Job Management
    const handleAddJob = async (jobData) => {
        const result = await addJob(jobData);
        if (result.success) {
            await loadAllData();
        }
        return result;
    };

    const handleUpdateJob = async (jobId, jobData) => {
        const result = await updateJob(jobId, jobData);
        if (result.success) {
            await loadAllData();
        }
        return result;
    };

    const handleDeleteJob = async (jobId) => {
        if (window.confirm('Are you sure you want to delete this job?')) {
            const result = await deleteJob(jobId);
            if (result.success) {
                await loadAllData();
            }
            return result;
        }
        return { success: false };
    };

    // Blog Management
    const handleAddBlog = async (blogData) => {
        const result = await addBlog(blogData);
        if (result.success) {
            await loadAllData();
        }
        return result;
    };

    const handleUpdateBlog = async (blogId, blogData) => {
        const result = await updateBlog(blogId, blogData);
        if (result.success) {
            await loadAllData();
        }
        return result;
    };

    const handleDeleteBlog = async (blogId) => {
        if (window.confirm('Are you sure you want to delete this blog post?')) {
            const result = await deleteBlog(blogId);
            if (result.success) {
                await loadAllData();
            }
            return result;
        }
        return { success: false };
    };

    // User Management
    const handleDeleteUser = async (userId) => {
        if (window.confirm('Are you sure you want to delete this user? This action cannot be undone.')) {
            const result = await deleteUser(userId);
            if (result.success) {
                await loadAllData();
            }
            return result;
        }
        return { success: false };
    };

    return {
        jobs,
        blogs,
        users,
        stats,
        loading,
        activeTab,
        setActiveTab,
        handleAddJob,
        handleUpdateJob,
        handleDeleteJob,
        handleAddBlog,
        handleUpdateBlog,
        handleDeleteBlog,
        handleDeleteUser,
        refreshData: loadAllData
    };
};